import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import FJArrowBlackIcon from '../../assets/back.svg?react';
import FJSubtitleStyleList from '../../config/FJSubtitleStyleList';
import useLocalStore from '../../hooks/useLocalStore';
import FJLocalStore from '../../store/FJLocalStore';
import FJSubtitleItem from './FJSubtitleItem';
import styles from './FJSubtitleList.module.less';

/** 与 `FJSubtitleItem`、`.rail` 网格一致：宽 + 列间距 */
const SUBTITLE_ITEM_STRIDE = 120 + 10;

interface FJSubtitleListProps {
    selectedId: string;
    onSelect: (id: string) => void;
}

export default function FJSubtitleList({ selectedId, onSelect }: FJSubtitleListProps) {
    useLocalStore();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [scrollSnaps, setScrollSnaps] = useState({ atStart: true, atEnd: true });

    const updateScrollSnaps = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const maxScroll = scrollWidth - clientWidth;
        const eps = 2;
        setScrollSnaps({
            atStart: scrollLeft <= eps,
            atEnd: maxScroll <= eps || scrollLeft >= maxScroll - eps,
        });
    }, []);

    useLayoutEffect(() => {
        updateScrollSnaps();
    }, [updateScrollSnaps]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }
        updateScrollSnaps();
        el.addEventListener('scroll', updateScrollSnaps, { passive: true });
        const ro = new ResizeObserver(updateScrollSnaps);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollSnaps);
            ro.disconnect();
        };
    }, [updateScrollSnaps]);

    const scrollByDir = useCallback((dir: 'prev' | 'next') => {
        const el = scrollerRef.current;
        if (!el) {
            return;
        }
        const cols = Math.max(1, Math.floor(el.clientWidth / SUBTITLE_ITEM_STRIDE));
        const delta = cols * SUBTITLE_ITEM_STRIDE;
        const reduceMotion =
            typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollBy({
            left: dir === 'next' ? delta : -delta,
            behavior: reduceMotion ? 'auto' : 'smooth',
        });
    }, []);

    return (
        <section className={styles.section} aria-label={FJLocalStore._('subtitle')}>
            <div className={styles.header}>
                <span className={styles.title}>{FJLocalStore._('subtitle')}</span>
                <div className={styles.nav}>
                    <button
                        type='button'
                        className={styles.arrow}
                        onClick={() => scrollByDir('prev')}
                        disabled={scrollSnaps.atStart}
                    >
                        <FJArrowBlackIcon className={styles.arrow_svg_prev} />
                    </button>
                    <button
                        type='button'
                        className={styles.arrow}
                        onClick={() => scrollByDir('next')}
                        disabled={scrollSnaps.atEnd}
                    >
                        <FJArrowBlackIcon className={styles.arrow_svg} />
                    </button>
                </div>
            </div>
            <div ref={scrollerRef} className={styles.scroll}>
                <div className={styles.rail}>
                    {FJSubtitleStyleList.map(style => (
                        <FJSubtitleItem
                            key={style.id}
                            styleItem={style}
                            selected={selectedId === style.id}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
